import { BrowserModule } from '@angular/platform-browser';
import { NgModule, LOCALE_ID } from '@angular/core';
import {
    HttpClient,
    HttpClientModule,
    HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AppHttpInterceptor } from './providers/http.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

// Components
import { LoginComponent } from '@components/login/login.component';
import { ActivateComponent } from '@components/login/activate/activate.component';
import { RememberPasswordComponent } from '@components/login/remember-password/remember-password.component';
import { HomeComponent } from '@components/pages/home/home.component';
import { EditProfileComponent } from '@components/pages/edit-profile/edit-profile.component';
import { DealPageComponent } from '@components/pages/deal-page/deal-page.component';
import { MeetingSelectorComponent } from '@components/pages/deal-page/meeting-selector/meeting-selector.component';
import { ProcessComponent } from '@components/pages/deal-page/process/process.component';
import { TriggerCardComponent } from '@components/pages/deal-page/trigger-card/trigger-card.component';
import { NotFoundComponent } from '@components/pages/not-found/not-found.component';
import { SideNavBarComponent } from '@components/shared/side-nav-bar/side-nav-bar.component';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { CustomPreviewComponent } from './components/shared/multiupload-files/custom-preview/custom-preview.component';
import { MeetingsManagementComponent } from '@components/shared/meetings-management/meetings-management.component';
import { NotificationsComponent } from '@components/shared/notifications/notifications.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';

// Libs
import { ChartsModule } from 'ng2-charts';
import { OnWacUiLibModule } from 'on-wac-ui-lib';
import { FlexiformsLibraryModule } from 'flexiforms-library';
import { BreadcrumbModule } from 'angular-crumbs';

// Services
import { PermissionService } from '@services/permissions.service';
import { AuthGuardService } from '@services/auth/auth-guard.service';
import { MeetingService } from '@services/metting.service';
import { DealService } from '@services/deal.service';
import { DealTriggerService } from '@services/dealTriggers.service';

// Helpers
import { FlexiformHelperService } from '@helpers/flexiform.helper';
import { HelpersService } from '@helpers/helpers';
import { DateHelperService } from '@helpers/date.helper';

// Shared components
import { ProgressBarComponent } from '@components/shared/progress-bar/progress-bar.component';
import { MultiuploadFilesComponent } from '@components/shared/multiupload-files/multiupload-files.component';

// Pipes
import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';

// Multilingual
import localeEs from '@angular/common/locales/es';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';

import { NotificationService } from '@services/notifications.service';
import { NoteService } from '@services/note.service';
import { SnackbarService } from '@services/snackbar.service';
import { NoteComponent } from '@components/pages/deal-page/note/note.component';
import { DocumentComponent } from './components/pages/deal-page/document/document.component';
import { DocumentService } from '@services/documents.service';
import { ConfirmModalComponent } from './components/shared/confirm-modal/confirm-modal.component';
import { ServerDocsService } from '@services/server-docs.service';
import { CRUDService } from '@services/api-calls/crud.service';

registerLocaleData(localeEs, 'es');
export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        ActivateComponent,
        RememberPasswordComponent,
        HomeComponent,
        SideNavBarComponent,
        EditProfileComponent,
        DealPageComponent,
        MeetingSelectorComponent,
        ProcessComponent,
        TriggerCardComponent,
        ProgressBarComponent,
        MultiuploadFilesComponent,
        NotFoundComponent,
        CustomPreviewComponent,
        MeetingsManagementComponent,
        NotificationsComponent,
        NoteComponent,
        DocumentComponent,
        ConfirmModalComponent,
    ],
    imports: [
        BrowserModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        AppRoutingModule,
        HttpClientModule,
        FormsModule,
        ReactiveFormsModule,
        ChartsModule,
        OnWacUiLibModule,
        FlexiformsLibraryModule,
        BreadcrumbModule,
        NgxDropzoneModule,
        MatDialogModule,
        BrowserAnimationsModule,
        MatExpansionModule,
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AppHttpInterceptor,
            multi: true,
        },
        {
            provide: LOCALE_ID,
            useValue: 'es',
        },
        PermissionService,
        AuthGuardService,
        MeetingService,
        DealService,
        DealTriggerService,
        FlexiformHelperService,
        HelpersService,
        DateHelperService,
        DatePipe,
        DecimalPipe,
        NotificationService,
        NoteService,
        SnackbarService,
        DocumentService,
        ServerDocsService,
        CRUDService,
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
